import Typography from "../misc/typography.tsx";
import { Twitter, Github, Linkedin, Mail, Globe } from "lucide-react";
import TopHeader from "../misc/topHeader.tsx";

const teamData = [
  {
    name: "AyeTee",
    role: "Lead Developer & Game Architect",
    image: "/images/teams/ayetee.svg",
    content:
      "A passionate full-stack developer with expertise in blockchain gaming and smart contracts. Leading the technical vision of Rumble and ensuring seamless gameplay mechanics.",
    expertise: ["Game Architecture", "Smart Contracts", "Full-Stack Development"],
    alt: "AyeTee profile",
    socials: {
      twitter: "https://twitter.com/AyeTee",
      linkedin: "https://linkedin.com/in/AyeTee",
      github: "https://github.com/AyeTee",
      email: "ayetee@rumble.game"
    },
  },
  {
    name: "Wayne",
    role: "Backend Engineer & Infrastructure Lead",
    image: "/images/teams/gunna.svg",
    content:
      "Specializes in scalable backend systems and blockchain infrastructure. Ensures Rumble's battle system runs smoothly and securely for all players.",
    expertise: ["Backend Systems", "DevOps", "Blockchain Integration"],
    alt: "Wayne profile",
    socials: {
      twitter: "https://twitter.com/Wayne",
      linkedin: "https://linkedin.com/in/Wayne",
      github: "https://github.com/Wayne",
      email: "wayne@rumble.game"
    },
  },
  {
    name: "Gunna",
    role: "Game Designer & Creative Director",
    image: "/images/teams/wayne.svg",
    content:
      "Creative mind behind Rumble's unique gameplay mechanics and elemental system. Brings extensive experience in game design and player engagement strategies.",
    expertise: ["Game Design", "UX/UI", "Character Development"],
    alt: "Gunna profile",
    socials: {
      twitter: "https://twitter.com/Gunna",
      linkedin: "https://linkedin.com/in/Gunna",
      github: "https://github.com/Gunna",
      email: "gunna@rumble.game"
    },
  },
];

const Team = () => {
  return (
    <div className="min-h-screen py-20 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-20%,rgba(99,102,241,0.15),rgba(0,0,0,0)_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_60%,rgba(245,158,11,0.1),rgba(0,0,0,0)_50%)]" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <TopHeader title="Meet Our Team" />
          <Typography>THE MASTERMINDS</Typography>
          <p className="mt-6 text-gray-400 text-lg max-w-2xl mx-auto">
            Meet the passionate individuals behind Rumble who are dedicated to creating
            an exceptional gaming experience in the world of elemental warfare.
          </p>
        </div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {teamData.map((member, index) => (
            <div 
              key={index}
              className="relative group bg-gaming-dark rounded-xl overflow-hidden border border-gray-800 
                transform transition-all duration-300 hover:-translate-y-2 hover:border-primary-500/50"
            >
              {/* Image Container */}
              <div className="relative h-64 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-gaming-dark to-transparent z-10" />
                <img
                  src={member.image}
                  alt={member.alt}
                  className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-110"
                />
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Name and Role */}
                <div className="mb-4">
                  <h2 className="text-2xl font-bold text-primary-400 gaming-text">
                    {member.name}
                  </h2>
                  <p className="text-amber-400/80 text-sm mt-1">{member.role}</p>
                </div>

                {/* Description */}
                <p className="text-gray-400 text-sm mb-4">
                  {member.content}
                </p>

                {/* Expertise */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-300 mb-2">Expertise</h3>
                  <div className="flex flex-wrap gap-2">
                    {member.expertise.map((skill, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 text-xs rounded-full bg-primary-500/20 text-primary-400 border border-primary-500/30"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Social Links */}
                <div className="flex items-center space-x-4 pt-4 border-t border-gray-800">
                  <a
                    href={member.socials.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-primary-400 transition-colors"
                  >
                    <Twitter className="w-5 h-5" />
                  </a>
                  <a
                    href={member.socials.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-primary-400 transition-colors"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <a
                    href={member.socials.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-primary-400 transition-colors"
                  >
                    <Github className="w-5 h-5" />
                  </a>
                  <a
                    href={`mailto:${member.socials.email}`}
                    className="text-gray-400 hover:text-primary-400 transition-colors"
                  >
                    <Mail className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Join the Team Section */}
        <div className="mt-20 text-center">
          <div className="max-w-2xl mx-auto bg-gaming-dark rounded-xl p-8 border border-gray-800">
            <h3 className="text-2xl font-bold text-primary-400 mb-4">Join Our Team</h3>
            <p className="text-gray-400 mb-6">
              Passionate about blockchain gaming? We're always looking for talented individuals
              to join our mission of revolutionizing the gaming industry.
            </p>
            <a
              href="mailto:careers@rumble.game"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors"
            >
              <Mail className="w-5 h-5" />
              <span>Get in Touch</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Team;
